import { fetchWithChatFeedback } from '../api/chatFeedbackRequired';
/**
 * Utilities for hash-based cache validation
 */

import { apiCall } from '../api/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Check if cached output is valid for current input
 * @param flowId Flow identifier
 * @param stageNum Stage number (1-6)
 * @param currentInput Current input data
 * @returns Object with cache_valid flag and cached_output data
 */
export async function checkCacheValidity(
  flowId: string | null | undefined,
  stageNum: number,
  currentInput: any
): Promise<{ cache_valid: boolean; cached_output: any }> {
  if (!flowId) {
    return { cache_valid: false, cached_output: null };
  }

  try {
    // Use direct fetch since the backend returns a plain response, not wrapped in ApiResponse
    const cacheHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    const cacheUserEmail = localStorage.getItem('userEmail') || (window as any).userEmail || '';
    if (cacheUserEmail) cacheHeaders['X-User-Email'] = cacheUserEmail;

    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}/check-cache`, {
      method: 'POST',
      headers: cacheHeaders,
      body: JSON.stringify(currentInput),
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.warn('Cache check failed:', error);
  }

  return { cache_valid: false, cached_output: null };
}

/**
 * Get stage data (input and output) from backend
 * @param flowId Flow identifier
 * @param stageNum Stage number (1-6)
 * @returns Stage data with input and output
 */
export async function getStageData(
  flowId: string | null | undefined,
  stageNum: number
): Promise<{ input: any; output: any } | null> {
  if (!flowId) {
    return null;
  }

  try {
    const userEmail = localStorage.getItem('userEmail') || (window as any).userEmail || '';
    const headers: Record<string, string> = {};
    if (userEmail) headers['X-User-Email'] = userEmail;

    // First try to get UI-ready format (skip for stages >= 100 which never have UI data)
    if (stageNum < 100) {
      try {
        const uiResponse = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}/ui`, {
          headers,
          credentials: 'include',
        });

        if (uiResponse.ok) {
          const uiData = await uiResponse.json();
          // The UI-cache endpoint intentionally returns HTTP 200 + `null`
          // when a historical stage has output but lacks the input needed to
          // rebuild its derived snapshot. That is a cache miss, not a usable
          // stage payload: continue to the raw endpoint so saved work (most
          // notably older Stage-6 vendor results) remains visible on reopen.
          if (uiData && typeof uiData === 'object' && !Array.isArray(uiData)) {
            console.log(`✅ Loaded UI-ready data for stage ${stageNum}`);
            return uiData as { input: any; output: any };
          }
        }
      } catch {
        // UI endpoint failed (e.g. CORS on 404) — fall through to raw format
      }
    }

    // Fallback to raw format
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}`, {
      headers,
      credentials: 'include',
    });
    
    if (!response.ok) {
      console.warn(`Failed to get stage ${stageNum} data: HTTP ${response.status}`);
      return null;
    }

    const data = await response.json();
    console.log(`⚠️ Using raw format for stage ${stageNum} (UI format not available)`);
    return data;
  } catch (error) {
    console.warn('Failed to get stage data:', error);
  }

  return null;
}

/**
 * Read the persisted stage record without consulting the derived UI cache.
 * Use this when a mutable draft is authoritative and a failed UI-cache write
 * must not replay an older editable value.
 */
export async function getRawStageData(
  flowId: string | null | undefined,
  stageNum: number
): Promise<{ input: any; output: any } | null> {
  if (!flowId) return null;

  try {
    const userEmail = localStorage.getItem('userEmail') || (window as any).userEmail || '';
    const headers: Record<string, string> = {};
    if (userEmail) headers['X-User-Email'] = userEmail;
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}`, {
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      console.warn(`Failed to get raw stage ${stageNum} data: HTTP ${response.status}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn('Failed to get raw stage data:', error);
    return null;
  }
}

/** Distinguish a genuinely absent stage from a failed read before auto-start. */
export async function getRawStageDataPresence(
  flowId: string | null | undefined,
  stageNum: number,
): Promise<{ status: 'found'; data: { input: any; output: any } } | { status: 'absent' | 'unavailable' }> {
  if (!flowId) return { status: 'unavailable' };
  try {
    const userEmail = localStorage.getItem('userEmail') || (window as any).userEmail || '';
    const headers: Record<string, string> = {};
    if (userEmail) headers['X-User-Email'] = userEmail;
    const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}`, {
      headers, credentials: 'include',
    });
    if (!response.ok) return { status: 'unavailable' };
    const data = await response.json();
    if (!data || typeof data !== 'object' || Array.isArray(data)
      || !('input' in data) || !('output' in data)) return { status: 'unavailable' };
    if (data.input === null && data.output === null) return { status: 'absent' };
    return { status: 'found', data };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * Stage number mapping for different stages
 *
 * Stage ranges:
 * - Market Research workflow: 1-12
 * - CLS workflow: 101-103 (internal)
 * - SOW Verification workflow: 200-201
 * - Wage Determination workflow: 301-304
 * - PNM workflow: 401
 * - Services workflow: 600-606
 */
export const STAGE_NUMBERS = {
  QUESTIONS: 1,
  MFR: 2,
  PRODUCTS: 3,
  PSC: 4,
  NAICS: 5,
  VENDORS: 6,
  SECTION_B: 7,
  SECTION_C: 8,
  SECTION_D: 9,
  SECTION_E: 10,
  SYNOPSIS: 11,
  VENDOR_BIDS: 12,
  MARKET_RESEARCH_DD2579: 13,
  MARKET_RESEARCH_BRAND_NAME_JA: 14,
  SOW_VERIFICATION: 201,  // Results stage (document upload uses stage 200)
  SOW_VERIFICATION_EDIT: 202,
  PNM: 401,
  CONSTRUCTION_ABSTRACT: 502,
  // Services workflow (600-series)
  SERVICES_SOW_UPLOAD: 600,            // SOW Upload & Source Docs (was 601)
  SERVICES_CONTEXT_QUESTIONS: 601,     // Context Questions (NEW)
  SERVICES_SUGGESTIONS: 602,           // Suggestions
  SERVICES_MANUAL_EDIT: 603,           // Manual Edit
  SERVICES_SALIENT_CHARS: 604,         // Salient Characteristics
  SERVICES_CODE_SELECTION: 605,        // Code Selection (NAICS + PSC)
  SERVICES_VENDOR_DISCOVERY: 606,      // Vendor Discovery
  // Services COMBO workflow (700-series) — Combined Synopsis/Solicitation
  SERVICES_COMBO_UPLOAD: 700,          // Document Upload
  SERVICES_COMBO_FORM: 701,            // Form (canonical completion stage; Export is a button)
  // Complex Services workflow — reuses the Services authoring band (600–606,
  // above) and adds the complex-only stages (607–616). Stage CODE ≠ stepper
  // order (see COMPLEX_SERVICES_STAGE_ORDER). `record` (609) is the terminal
  // completion stage. Namespaced under the `complex_services` type so 600–606
  // do not collide with `services`.
  COMPLEX_SERVICES_COMPLIANCE: 607,
  COMPLEX_SERVICES_DD2579: 608,
  COMPLEX_SERVICES_RECORD: 609,        // TERMINAL — completion iff 609 saved
  COMPLEX_SERVICES_PWS_DIFF: 610,
  COMPLEX_SERVICES_OPTIONS: 611,
  COMPLEX_SERVICES_REQVAL: 612,
  COMPLEX_SERVICES_QASP: 613,
  COMPLEX_SERVICES_SOLICITATION: 614,
  COMPLEX_SERVICES_CLEARANCE: 615,
  COMPLEX_SERVICES_IGE: 616,
} as const;

/**
 * Maximum stage number for each workflow type
 * Market Research: 1-13 (the DD/DAF 2579 final stage is conditional)
 * CLS: 101-103
 * SOW Verification: 200-201
 * Wage Determination: 301-304
 * PNM: 401
 */
export const MAX_STAGE_NUMBER = 14;  // Market Research backend stage identifier
export const MARKET_RESEARCH_MAX_STAGES = 14;
export const CLS_MAX_STAGES = 3;
export const SOW_VERIFICATION_MAX_STAGES = 3;
export const WAGE_DETERMINATION_MAX_STAGES = 2;
export const PNM_MAX_STAGES = 2;
export const FOPR_MAX_STAGES = 3;
export const CONSTRUCTION_ABSTRACT_MAX_STAGES = 1;
export const SERVICES_MAX_STAGES = 7;
export const SERVICES_COMBO_MAX_STAGES = 2;  // Upload (700) + Form (701)
export const GPC_MAX_STAGES = 3;  // Questions (800) + Salient Chars (801) + Product Discovery (802)
/**
 * Length of the Complex Services stepper (the FULL master order — reused
 * authoring 600–606 + complex-only 607–616). Used ONLY for the fixed stepper
 * length; the PROGRESS denominator is the per-branch enabled-stage count
 * (`applicableComplexStages(flags).length`, spec §5.6), NOT this constant.
 */
export const COMPLEX_SERVICES_STAGE_COUNT = 17;
